import { useState, type ReactNode } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CallEnd01Icon,
  DialpadCircle01Icon,
  Logout03Icon,
  Mic01Icon,
  MicOff01Icon,
  PauseIcon,
  User02Icon,
  UserRemove01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import type { CallTransferAction } from "api/voice";
import { Avatar } from "shared/components/Avatar";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { cx } from "shared/utils";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import { CallKeypad } from "./CallKeypad";
import { ControlButton } from "./ControlButton";
import {
  firstName,
  isPhoneLabel,
  useSoftphone,
  type ActiveTransfer
} from "./SoftphoneProvider";
import { useElapsed } from "./useElapsed";

const pad = (value: number) => String(value).padStart(2, "0");
const clock = (seconds: number) =>
  `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;

// How the card names the person a teammate is adding (CRM-1818), the way the
// Transfer call view's rows named them: "Maintenance · (916) 555-0142".
const targetSub = (transfer: ActiveTransfer): string => {
  if (transfer.targetKind === "team") return "Rings in Boom";
  if (transfer.targetKind === "typed") return "Typed number";
  const number = transfer.targetNumber
    ? formatPhoneDisplay(transfer.targetNumber)
    : "";
  if (transfer.targetNote && number)
    return `${transfer.targetNote} · ${number}`;
  return number || transfer.targetNote || "Transfer list";
};

interface ConsultCopy {
  status: string;
  tone: "muted" | "live" | "error";
  hint: string;
}

const consultCopy = (
  transfer: ActiveTransfer,
  caller: string,
  elapsed: number | null
): ConsultCopy => {
  const first = firstName(transfer.targetName);
  const callerFirst = firstName(caller);
  switch (transfer.phase) {
    case "consulting":
      return {
        status: elapsed === null ? "Connected" : clock(elapsed),
        tone: "live",
        hint: `You're talking to ${first}. ${callerFirst} can't hear you.`
      };
    case "consult-not-answered":
      return {
        status: "Didn't answer",
        tone: "error",
        hint: `${first} didn't answer. ${callerFirst} is still on hold.`
      };
    case "consult-destination-left":
      return {
        status: "Hung up",
        tone: "error",
        hint: `${first} hung up. ${callerFirst} is still on hold.`
      };
    default:
      return {
        status: "Ringing…",
        tone: "muted",
        hint: `${callerFirst} hears hold music while ${first}'s phone rings.`
      };
  }
};

// Run a card action, keeping which one is in flight and what went wrong.
const useTransferAction = () => {
  const { transferAction } = useSoftphone();
  const [pending, setPending] = useState<CallTransferAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: CallTransferAction) => {
    setPending(action);
    setError(null);
    try {
      await transferAction(action);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  };

  return { pending, error, run };
};

/**
 * Talk first (CRM-1747, mockup "Transferring · talk first"): the caller waits
 * on hold while the teammate speaks to the destination, then hands the caller
 * over (Complete transfer), introduces them first (Merge, CRM-1818) or takes
 * them back (Back to caller). End call ends it for all three.
 *
 * Add call (CRM-1818, mockup "Adding: talking to them first") is the same
 * consult, with Merge as the way forward.
 */
export function ConsultView({
  transfer,
  callerName
}: {
  transfer: ActiveTransfer;
  callerName: string;
}) {
  const { hangup } = useSoftphone();
  const { pending, error, run } = useTransferAction();
  const elapsed = useElapsed(
    transfer.phase === "consulting" ? transfer.consultAnsweredAt : undefined
  );
  const copy = consultCopy(transfer, callerName, elapsed);
  const answered = transfer.phase === "consulting";
  const adding = transfer.mode === "add";

  return (
    <div className="w-full text-left">
      <CardLabel>
        {adding ? "Adding to call" : "Transferring · talk first"}
      </CardLabel>

      <div className="mt-4 space-y-2">
        <PartyRow
          name={callerName}
          sub="Caller"
          status={
            <span className="inline-flex items-center gap-1 text-content-400">
              <HugeiconsIcon icon={PauseIcon} size={14} />
              On hold
            </span>
          }
        />
        <PartyRow
          name={transfer.targetName}
          sub={adding ? targetSub(transfer) : "Transfer to"}
          status={
            <span
              className={cx(
                "tabular-nums",
                copy.tone === "live" && "text-success-500",
                copy.tone === "error" && "text-danger-500",
                copy.tone === "muted" && "text-content-400"
              )}
            >
              {copy.status}
            </span>
          }
        />
      </div>

      <p className="mt-3 text-xs text-content-400">{copy.hint}</p>
      {error ? <p className="mt-2 text-xs text-danger-500">{error}</p> : null}

      <div className="mt-5 space-y-2">
        {answered && !adding ? (
          <Button
            className="w-full"
            size={ButtonSize.BASE}
            loading={pending === "complete"}
            disabled={pending !== null}
            onClick={() => void run("complete")}
          >
            Complete transfer
          </Button>
        ) : null}
        {answered ? (
          <Button
            className="w-full"
            size={ButtonSize.BASE}
            variant={adding ? ButtonVariant.PRIMARY : ButtonVariant.BASE}
            loading={pending === "merge"}
            disabled={pending !== null}
            onClick={() => void run("merge")}
          >
            {adding ? "Merge" : `Merge to introduce ${firstName(callerName)}`}
          </Button>
        ) : null}
        <Button
          className="w-full"
          size={ButtonSize.BASE}
          variant={answered ? ButtonVariant.BASE : ButtonVariant.PRIMARY}
          loading={pending === "back"}
          disabled={pending !== null}
          onClick={() => void run("back")}
        >
          Back to caller
        </Button>
      </div>

      <div className="mt-5 flex justify-center">
        <ControlButton
          label="End call"
          onClick={hangup}
          circleClassName="bg-danger-500 text-white hover:bg-danger-600"
          icon={CallEnd01Icon}
        />
      </div>
    </div>
  );
}

/**
 * The 3-way call (CRM-1818, mockup "3-way call"): the caller and the person
 * the teammate added, with how long each has been on. Remove hangs up the
 * added person (the caller's row has none); Leave call keeps the other two
 * talking; End for everyone ends it for all three. Once the caller hung up
 * only Mute, Keypad and End call are left.
 */
export function ThreeWayView({
  transfer,
  callerName,
  callerElapsedLabel,
  keypadOpen,
  onKeypadOpenChange
}: {
  transfer: ActiveTransfer;
  callerName: string;
  // The call's own clock, ticked by the overlay.
  callerElapsedLabel: string;
  keypadOpen: boolean;
  onKeypadOpenChange: (open: boolean) => void;
}) {
  const { sendDtmf } = useSoftphone();
  const { pending, error, run } = useTransferAction();
  const addedElapsed = useElapsed(transfer.consultAnsweredAt);
  const callerLeft = transfer.phase === "three-way-caller-left";
  const first = firstName(transfer.targetName);

  return (
    <div className="w-full text-left">
      <CardLabel>3-way call</CardLabel>

      <div className="mt-4 space-y-2">
        <PartyRow
          name={callerName}
          sub="Caller"
          status={
            <ThreeWayStatus
              label={callerLeft ? "Hung up" : callerElapsedLabel}
              tone={callerLeft ? "error" : "live"}
            />
          }
        />
        <PartyRow
          name={transfer.targetName}
          sub={targetSub(transfer)}
          status={
            <ThreeWayStatus
              label={addedElapsed === null ? "Connected" : clock(addedElapsed)}
              tone="live"
            />
          }
          onRemove={callerLeft ? undefined : () => void run("remove")}
          removeDisabled={pending !== null}
        />
      </div>

      <p className="mt-3 text-xs text-content-400">
        {callerLeft
          ? `${firstName(callerName)} hung up. You're still on with ${first}.`
          : "Everyone can hear each other. Keypad tones are heard by everyone."}
      </p>
      {error ? <p className="mt-2 text-xs text-danger-500">{error}</p> : null}

      {keypadOpen ? (
        <CallKeypad
          onDigit={sendDtmf}
          onClose={() => onKeypadOpenChange(false)}
        />
      ) : null}

      <ThreeWayControls
        callerLeft={callerLeft}
        busy={pending !== null}
        keypadOpen={keypadOpen}
        onToggleKeypad={() => onKeypadOpenChange(!keypadOpen)}
        onLeave={() => void run("leave")}
      />
      {callerLeft ? null : (
        <p className="mt-3 text-center text-xs text-content-400">
          Leave call keeps {firstName(callerName)} and {first} talking.
        </p>
      )}
    </div>
  );
}

function ThreeWayStatus({
  label,
  tone
}: {
  label: string;
  tone: "live" | "error";
}) {
  return (
    <span
      className={cx(
        "tabular-nums",
        tone === "live" ? "text-success-500" : "text-danger-500"
      )}
    >
      {label}
    </span>
  );
}

// Mute and Keypad, then Leave call and End for everyone — or only End call
// once the caller hung up.
function ThreeWayControls({
  callerLeft,
  busy,
  keypadOpen,
  onToggleKeypad,
  onLeave
}: {
  callerLeft: boolean;
  // A card action is in flight.
  busy: boolean;
  keypadOpen: boolean;
  onToggleKeypad: () => void;
  onLeave: () => void;
}) {
  const { hangup, isMuted, toggleMute } = useSoftphone();
  return (
    <>
      <div className="mt-6 flex items-start justify-center gap-6">
        <ControlButton
          label={isMuted ? "Unmute" : "Mute"}
          onClick={toggleMute}
          circleClassName={cx(
            "text-content-1000 hover:bg-alpha-100",
            isMuted ? "bg-alpha-100" : "bg-alpha-40"
          )}
          icon={isMuted ? MicOff01Icon : Mic01Icon}
        />
        <ControlButton
          label="Keypad"
          onClick={onToggleKeypad}
          pressed={keypadOpen}
          circleClassName={cx(
            "text-content-1000 hover:bg-alpha-100",
            keypadOpen ? "bg-alpha-100" : "bg-alpha-40"
          )}
          icon={DialpadCircle01Icon}
        />
      </div>

      <div className="mt-5 flex items-start justify-center gap-10">
        {callerLeft ? null : (
          <ControlButton
            label="Leave call"
            onClick={onLeave}
            disabled={busy}
            circleClassName="bg-alpha-40 text-content-1000 hover:bg-alpha-100"
            icon={Logout03Icon}
          />
        )}
        <ControlButton
          label={callerLeft ? "End call" : "End for everyone"}
          onClick={hangup}
          circleClassName="bg-danger-500 text-white hover:bg-danger-600"
          icon={CallEnd01Icon}
        />
      </div>
    </>
  );
}

function CardLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-medium uppercase tracking-wide text-content-300">
      {children}
    </p>
  );
}

function PartyRow({
  name,
  sub,
  status,
  onRemove,
  removeDisabled
}: {
  name: string;
  sub: string;
  status: ReactNode;
  // CRM-1818: hangs this person up on a 3-way call.
  onRemove?: () => void;
  removeDisabled?: boolean;
}) {
  // A number has no initials to show.
  const isNumber = isPhoneLabel(name);
  return (
    <div className="flex items-center gap-3 rounded-xl bg-alpha-40 p-3">
      <Avatar
        name={isNumber ? undefined : name}
        size={36}
        fallbackText={<HugeiconsIcon icon={User02Icon} size={18} />}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-content-1000">
          {name}
        </p>
        <p className="truncate text-xs text-content-400">{sub}</p>
      </div>
      <span className="shrink-0 text-xs font-medium">{status}</span>
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${name}`}
          title="Remove from call"
          onClick={onRemove}
          disabled={removeDisabled}
          className="grid size-8 shrink-0 place-items-center rounded-full text-content-400 transition hover:bg-alpha-100 hover:text-danger-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <HugeiconsIcon icon={UserRemove01Icon} size={18} />
        </button>
      ) : null}
    </div>
  );
}
